import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Play } from 'lucide-react'

type Fund = { id: string; name: string; ticker: string; source: string }

type SeriesPoint = { date: string; realised: number | null; var: number | null; es: number | null; exceedance: boolean }
type DDPoint = { date: string; value: number | null }

type RegimeStat = {
  regime: string
  n_obs: number
  n_exceedances: number
  expected: number | null
  exceedance_rate: number | null
  kupiec_p_value: number | null
}

type BacktestResp = {
  run_id: number; fund_id: string
  horizon: number; confidence: number; window_days: number
  n_obs: number; n_exceedances: number
  expected_exceedances: number | null
  exceedance_rate: number | null
  kupiec_p_value: number | null
  ytd_max_drawdown: number | null
  series: SeriesPoint[]
  drawdown: DDPoint[]
  regime_stats?: RegimeStat[]
}

export default function RiskBacktest() {
  const [search, setSearch] = useState('')
  const [fundId, setFundId] = useState('')
  const [horizon, setHorizon] = useState(5)
  const [confidence, setConfidence] = useState(0.99)
  const [windowDays, setWindowDays] = useState(250)

  const fundsQ = useQuery<Fund[]>({
    queryKey: ['fa-funds'],
    queryFn: () => api.get('/fund-analysis/funds').then(r => r.data),
  })

  const run = useMutation<BacktestResp, unknown>({
    mutationFn: () => api.post('/risk-stats/backtest', {
      fund_id: fundId, horizon, confidence, window_days: windowDays,
    }).then(r => r.data),
  })

  const filteredFunds = useMemo(() => {
    if (!fundsQ.data) return []
    const s = search.toLowerCase()
    return fundsQ.data.filter(f =>
      !s || f.name?.toLowerCase().includes(s) || f.ticker?.toLowerCase().includes(s)
    ).slice(0, 300)
  }, [fundsQ.data, search])

  const pct = (v: number | null | undefined, d = 2) => v == null ? '—' : `${(v * 100).toFixed(d)}%`

  // Build a simple inline SVG plot for realised vs predicted VaR
  const chart = useMemo(() => {
    const pts = run.data?.series ?? []
    if (pts.length === 0) return null
    const width = 860, height = 220, pad = 28
    const xs = pts.map((_, i) => i)
    const ys = pts.flatMap(p => [p.realised ?? 0, p.var ?? 0, p.es ?? 0])
    const yMin = Math.min(...ys, -0.001)
    const yMax = Math.max(...ys, 0.001)
    const xMap = (i: number) => pad + (i / (xs.length - 1 || 1)) * (width - 2 * pad)
    const yMap = (v: number) => height - pad - ((v - yMin) / (yMax - yMin || 1)) * (height - 2 * pad)
    const realPath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xMap(i)} ${yMap(p.realised ?? 0)}`).join(' ')
    const varPath  = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xMap(i)} ${yMap(p.var  ?? 0)}`).join(' ')
    const esPath   = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xMap(i)} ${yMap(p.es   ?? 0)}`).join(' ')
    const exceedances = pts
      .map((p, i) => p.exceedance ? { x: xMap(i), y: yMap(p.realised ?? 0) } : null)
      .filter((x): x is { x: number; y: number } => x !== null)
    const yZero = yMap(0)
    return (
      <svg width={width} height={height} className="w-full">
        <line x1={pad} y1={yZero} x2={width-pad} y2={yZero} stroke="currentColor" strokeOpacity="0.2" />
        <path d={esPath}  stroke="#f97316" fill="none" strokeWidth={1} strokeOpacity="0.6" />
        <path d={varPath} stroke="#ef4444" fill="none" strokeWidth={1.4} />
        <path d={realPath} stroke="#0ea5e9" fill="none" strokeWidth={1.4} />
        {exceedances.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={2.5} fill="#dc2626" />
        ))}
      </svg>
    )
  }, [run.data])

  const ddChart = useMemo(() => {
    const pts = run.data?.drawdown ?? []
    if (pts.length === 0) return null
    const width = 860, height = 140, pad = 28
    const xMap = (i: number) => pad + (i / (pts.length - 1 || 1)) * (width - 2 * pad)
    const ys = pts.map(p => p.value ?? 0)
    const yMin = Math.min(...ys, 0)
    const yMap = (v: number) => pad + ((0 - v) / (-yMin || 1)) * (height - 2 * pad)
    const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xMap(i)} ${yMap(p.value ?? 0)}`).join(' ')
    return (
      <svg width={width} height={height} className="w-full">
        <path d={d} stroke="#7c3aed" fill="#7c3aed" fillOpacity="0.15" strokeWidth={1.4} />
      </svg>
    )
  }, [run.data])

  const ytdInside = (() => {
    if (!run.data?.ytd_max_drawdown) return null
    // Compare ytd_dd to the last predicted VaR (horizon-matched); reasonable quick verdict
    const last = run.data.series.length > 0 ? run.data.series[run.data.series.length - 1].var : null
    if (last == null) return null
    // VaR is negative; ytd_dd is negative. "inside" = ytd_dd > VaR
    return (run.data.ytd_max_drawdown ?? 0) >= last
  })()

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Risk Diagnostics — VaR backtest</h1>
        <p className="text-muted-foreground">
          Rolling historical VaR / ES vs realised horizon returns. Compares YTD drawdown to the predicted risk band
          and runs a Kupiec unconditional coverage test on the exceedance count.
        </p>
      </div>

      <div className="bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            placeholder="Search by name or ticker..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input flex-1"
          />
          <select
            value={fundId}
            onChange={e => setFundId(e.target.value)}
            className="input w-full md:w-[28rem]"
          >
            <option value="">-- Select fund --</option>
            {filteredFunds.map(f => (
              <option key={f.id} value={f.id}>{f.name} ({f.ticker}) [{f.source}]</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-3 items-center text-sm">
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Horizon</span>
            <select value={horizon} onChange={e => setHorizon(Number(e.target.value))} className="input">
              <option value={1}>1d</option>
              <option value={5}>5d</option>
              <option value={21}>21d</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Confidence</span>
            <select value={confidence} onChange={e => setConfidence(Number(e.target.value))} className="input">
              <option value={0.95}>95%</option>
              <option value={0.99}>99%</option>
              <option value={0.995}>99.5%</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Rolling window</span>
            <input
              type="number"
              value={windowDays}
              onChange={e => setWindowDays(Math.max(60, Number(e.target.value) || 250))}
              className="input w-24"
            />
          </label>
          <button
            type="button"
            disabled={!fundId || run.isPending}
            onClick={() => run.mutate()}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-40"
          >
            {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run backtest
          </button>
        </div>
        {run.isError && (
          <div className="text-sm text-destructive">
            {String((run.error as any)?.response?.data?.detail ?? run.error)}
          </div>
        )}
      </div>

      {run.data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Metric label="n observations"    value={String(run.data.n_obs)} />
            <Metric label="Exceedances"       value={`${run.data.n_exceedances} / ${Math.round(run.data.expected_exceedances ?? 0)}`} />
            <Metric label="Rate (vs nominal)" value={run.data.exceedance_rate == null ? '—' : `${(run.data.exceedance_rate * 100).toFixed(2)}%`} />
            <Metric label="Kupiec p-value"    value={run.data.kupiec_p_value == null ? '—' : run.data.kupiec_p_value.toFixed(3)} />
            <Metric label="YTD max drawdown"  value={pct(run.data.ytd_max_drawdown)} />
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">
                Realised {run.data.horizon}-day return vs predicted VaR{Math.round(run.data.confidence * 100)} / ES{Math.round(run.data.confidence * 100)}
              </h2>
              <div className="text-xs text-muted-foreground">
                <span className="inline-block w-3 h-0.5 bg-[#0ea5e9] align-middle mr-1" /> realised
                <span className="inline-block w-3 h-0.5 bg-[#ef4444] align-middle mx-2" /> VaR
                <span className="inline-block w-3 h-0.5 bg-[#f97316] align-middle mx-1" /> ES
                <span className="inline-block w-2 h-2 rounded-full bg-[#dc2626] align-middle mx-2" /> exceedance
              </div>
            </div>
            <div className="overflow-x-auto">{chart}</div>
          </div>

          {(run.data.regime_stats?.length ?? 0) > 0 && (
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="font-semibold mb-3">Regime-conditional calibration (SPX vol terciles)</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3">Regime</th>
                    <th className="text-right py-2 px-3">n</th>
                    <th className="text-right py-2 px-3">Exceedances</th>
                    <th className="text-right py-2 px-3">Expected</th>
                    <th className="text-right py-2 px-3">Rate</th>
                    <th className="text-right py-2 px-3">Kupiec p</th>
                  </tr>
                </thead>
                <tbody>
                  {run.data.regime_stats!.map(r => {
                    const verdict = r.kupiec_p_value == null ? '—'
                      : r.kupiec_p_value >= 0.05 ? 'pass' : 'fail'
                    return (
                      <tr key={r.regime} className="border-b border-border/50">
                        <td className="py-2 px-3 font-mono text-xs">{r.regime}</td>
                        <td className="py-2 px-3 text-right font-mono text-xs">{r.n_obs}</td>
                        <td className="py-2 px-3 text-right font-mono text-xs">{r.n_exceedances}</td>
                        <td className="py-2 px-3 text-right font-mono text-xs">{r.expected == null ? '—' : r.expected.toFixed(1)}</td>
                        <td className="py-2 px-3 text-right font-mono text-xs">{r.exceedance_rate == null ? '—' : (r.exceedance_rate * 100).toFixed(2) + '%'}</td>
                        <td className={'py-2 px-3 text-right font-mono text-xs ' + (verdict === 'pass' ? 'text-green-600' : verdict === 'fail' ? 'text-red-600' : '')}>
                          {r.kupiec_p_value == null ? '—' : r.kupiec_p_value.toFixed(3)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <div className="text-xs text-muted-foreground mt-3">
                A well-calibrated risk model should pass Kupiec (p ≥ 0.05) in every regime, not just the full sample.
                Failure in high-vol regime typically means the rolling historical tail is too thin for crisis conditions.
              </div>
            </div>
          )}

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Drawdown history</h2>
              <div className="text-xs text-muted-foreground">
                {ytdInside === true && <span className="text-green-600">YTD DD is within the predicted VaR band.</span>}
                {ytdInside === false && <span className="text-red-600">YTD DD has pierced the predicted VaR band.</span>}
              </div>
            </div>
            <div className="overflow-x-auto">{ddChart}</div>
          </div>
        </>
      )}
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-xl font-semibold mt-1 font-mono">{value}</div>
    </div>
  )
}
